import type { RuleSet } from '../rules';
import {
  type Building,
  levelDef,
  levelTitle,
  type PlayerId,
  type SimState,
  toMilli,
  unitAmounts,
} from '../sim';
import { upgradeFraction } from '../sim/levels';
import { UNDER_ATTACK_TICKS } from '../sim/placement';
import { clockLabel } from './matchClock';

/** Sim seconds per tick (the sim steps in 100 ms). */
const TICK_SECONDS = 0.1;

/** Seconds an upgrade stays paused after the last hit on its building. */
export const RESUME_SECONDS = UNDER_ATTACK_TICKS * TICK_SECONDS;

export interface UpgradeProgress {
  /** The level being upgraded to. */
  level: number;
  /** That level's title ("Stronghold"). */
  title: string;
  /** 0..1. */
  fraction: number;
  /** Seconds of progress still to go (it only counts down while not `paused`). */
  secondsLeft: number;
  /** The building was hit recently: progress is held. */
  paused: boolean;
  /** What Cancel gives back (whole units; `paid` in full). */
  refund: Record<string, number>;
}

/**
 * An upgrade in progress at `b`, for its owner only: a rival's is a secret
 * (`Building.upgrading`, docs/mechanics/fog.md), so anything not `playerId`'s
 * gives null, as does a building not upgrading.
 */
export function upgradeProgress(
  state: SimState,
  rules: RuleSet,
  b: Building,
  playerId: PlayerId
): UpgradeProgress | null {
  const up = b.upgrading;
  if (!up || b.owner !== playerId) return null;
  const def = rules.buildings[b.type];
  const next = def && levelDef(def, up.level);
  if (!def || !next) return null;
  return {
    level: up.level,
    title: levelTitle(def, up.level),
    fraction: upgradeFraction(rules, b),
    secondsLeft: Math.max(0, (toMilli(next.upgradeTime) - up.progress) / toMilli(1)),
    paused: b.lastDamagedTick !== undefined && state.tick - b.lastDamagedTick < UNDER_ATTACK_TICKS,
    refund: Object.fromEntries(
      Object.entries(unitAmounts(up.paid)).filter(([, amount]) => amount > 0)
    ),
  };
}

/** 31 → "0:31", 75 → "1:15" (whole seconds, rounded up). */
export function clockText(seconds: number): string {
  return clockLabel(Math.max(0, Math.ceil(seconds)));
}
